/*
 * Retailers section - switchable tabs (WAI-ARIA). Hoists each retailer-tab
 * block's <button role="tab"> into .retailers__tablist, then wires click +
 * roving-tabindex keyboard nav. Scoped per instance; safe to re-init in the
 * Theme Editor (AbortController tears listeners down on disconnect).
 */
(function () {
  if (customElements.get('retailers-tabs')) return;

  var toArray = function (nodeList) {
    return Array.prototype.slice.call(nodeList);
  };

  class RetailersTabs extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('.retailers__tablist');
      this.tabs = toArray(this.querySelectorAll('[role="tab"]'));
      this.panels = toArray(this.querySelectorAll('[role="tabpanel"]'));
      // Static markup, not hoisted; runs the same reset as the tab close ("X").
      this.backButton = this.querySelector('.retailers__back');

      if (!this.tablist || this.tabs.length === 0 || this.panels.length === 0) {
        return;
      }

      // Section ids to hide once a tab opens (data-hide-sections, one per
      // line or comma-separated).
      this.hideTargets = (this.dataset.hideSections || '')
        .split(/[\s,]+/)
        .map(function (value) { return value.trim(); })
        .filter(Boolean);

      // Move each tab button into the tablist (document order) and clear the
      // pre-JS inline `display: none`. Panels stay hidden until activated.
      this.tabs.forEach(function (tab) {
        tab.hidden = false;
        tab.style.removeProperty('display');
        this.tablist.appendChild(tab);
      }, this);

      this.classList.add('is-enhanced');

      this.controller = new AbortController();
      var options = { signal: this.controller.signal };

      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function (event) {
          // Close ("X") on the active tab resets the section; anything else
          // selects the tab.
          // if (
          //   event.target.closest('.retailers__tab__close') &&
          //   tab.getAttribute('aria-selected') === 'true'
          // ) {
          //   this.deselectAll();
          //   tab.focus();
          // } else {
          //
          // }
          this.activate(tab);
        }.bind(this), options);
        tab.addEventListener('keydown', this.onKeydown.bind(this), options);
      }, this);

      if (this.backButton) {
        this.backButton.addEventListener('click', function () {
          // Capture the focus target before deselectAll() clears aria-selected.
          var target = this.tabs.filter(function (tab) {
            return tab.getAttribute('aria-selected') === 'true';
          })[0] || this.tabs[0];
          this.deselectAll();
          if (target) target.focus();
        }.bind(this), options);
      }

      var preselected = this.tabs.filter(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      })[0];

      if (preselected) {
        this.activate(preselected, false);
      } else {
        // No tab selected: deselect all (each stays tabbable), hide panels.
        this.tabs.forEach(function (tab) {
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('tabindex', '0');
        });
        this.panels.forEach(function (panel) {
          panel.hidden = true;
        });
        this.setBackButtonVisible(false);
      }
    }

    // Show/hide the back button. Null-safe (absent when the section has no tabs).
    setBackButtonVisible(visible) {
      if (this.backButton) this.backButton.hidden = !visible;
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    activate(tab, focusTab) {
      if (focusTab === undefined) focusTab = true;

      this.tabs.forEach(function (item) {
        var selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.setAttribute('tabindex', selected ? '0' : '-1');
      });

      var controls = tab.getAttribute('aria-controls');
      this.panels.forEach(function (panel) {
        panel.hidden = panel.id !== controls;
      });

      this.hideLinkedSections();
      this.setBackButtonVisible(true);

      if (focusTab) tab.focus();
    }

    // Hide data-hide-sections targets (bare id or shopify-section-<id>). Idempotent.
    hideLinkedSections() {
      if (!this.hideTargets || this.hideTargets.length === 0) return;

      this.hideTargets.forEach(function (id) {
        var el = document.getElementById(id) ||
          document.getElementById('shopify-section-' + id);
        if (el) el.style.display = 'none';
      });
    }

    // Inverse of hideLinkedSections(). Idempotent.
    showLinkedSections() {
      if (!this.hideTargets || this.hideTargets.length === 0) return;

      this.hideTargets.forEach(function (id) {
        var el = document.getElementById(id) ||
          document.getElementById('shopify-section-' + id);
        if (el) el.style.removeProperty('display');
      });
    }

    // Reset to the initial "no tab selected" view.
    deselectAll() {
      this.tabs.forEach(function (tab) {
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '0');
      });
      this.panels.forEach(function (panel) {
        panel.hidden = true;
      });
      this.showLinkedSections();
      this.setBackButtonVisible(false);
    }

    onKeydown(event) {
      var index = this.tabs.indexOf(event.target);
      if (index === -1) return;

      // Escape = same reset as the close ("X"); keep focus on the open tab.
      if (event.key === 'Escape') {
        var selected = this.tabs.filter(function (tab) {
          return tab.getAttribute('aria-selected') === 'true';
        })[0];
        if (!selected) return;
        event.preventDefault();
        this.deselectAll();
        selected.focus();
        return;
      }

      var last = this.tabs.length - 1;
      var next = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activate(this.tabs[next]);
    }
  }

  customElements.define('retailers-tabs', RetailersTabs);
})();
